/* eslint-disable react-hooks/exhaustive-deps */
import {
  MINC_DATA_PATTERN,
  decodeS3Key,
  fetchSignedUrl,
  keyFromFileUrl,
} from './utils';
import { urlLabel } from '@/enum/PermissionEnum';
import fetchHelper from '@/helpers/FetchHelper';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

/**
 * Landing page for the "Minc Plato File Bridge" browser extension.
 *
 * A doctor clicks a mincdata file link inside Plato; the extension blocks the
 * direct S3 link and sends them here instead, with the S3 key as the path:
 *   /#/download-plato-files/Staging/Combined%20IR%20and%20Backrooms/x.pdf
 * Not logged in -> WrapperPrivateRoute parks the URL on /login?redirect=...
 * and WrapperAuthRoute brings them back here afterwards.
 *
 * The extension never holds credentials: this page is where the doctor is
 * authenticated and where the BE issues the signed URL. Without the extension
 * the same flow works by pasting the S3 URL as ?fileUrl=.
 */

const STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  DONE: 'done',
  ERROR: 'error',
};

const DownloadPlatoFiles = () => {
  const { pathname, search } = useLocation();
  const accessToken = useSelector((state) => state.auth.accessToken);

  // Primary form: the key is the rest of the path. Fallback: a pasted S3 URL.
  const pathPrefix = `/${urlLabel.downloadPlatoFiles}/`;
  const fileUrl = (new URLSearchParams(search).get('fileUrl') || '').trim();
  const fileName = pathname.startsWith(pathPrefix)
    ? decodeS3Key(pathname.slice(pathPrefix.length))
    : keyFromFileUrl(fileUrl);

  const [status, setStatus] = useState(STATUS.IDLE);
  const [errorMessage, setErrorMessage] = useState('');

  const download = useCallback(async () => {
    if (!fileName) {
      setStatus(STATUS.ERROR);
      setErrorMessage(
        'No file was provided. Open this page from a file link in Plato.'
      );
      return;
    }
    // Reject traversal, and anything pasted that is not a Minc file link.
    if (
      fileName.split('/').includes('..') ||
      (!!fileUrl && !MINC_DATA_PATTERN.test(fileUrl))
    ) {
      setStatus(STATUS.ERROR);
      setErrorMessage('This link is not a Minc file and cannot be downloaded.');
      return;
    }

    setStatus(STATUS.LOADING);
    setErrorMessage('');

    // This route renders with noLayout, so PrivateLayout - which is what
    // normally installs the Authorization header - never mounts. Without this
    // the request goes out unauthenticated, and Api.js logs the user out on the
    // 401 that comes back.
    fetchHelper.addDefaultHeader('Authorization', `Bearer ${accessToken}`);

    const signedUrl = await fetchSignedUrl(fileName);
    if (!signedUrl) {
      setStatus(STATUS.ERROR);
      setErrorMessage(
        'Could not get a download link for this file. It may have been moved, or you may not have access to it.'
      );
      return;
    }

    setStatus(STATUS.DONE);
    // Signed S3 URLs are cross-origin, so an <a download> attribute is ignored;
    // navigating is what actually opens/downloads the file.
    window.location.assign(signedUrl);
  }, [fileName, fileUrl, accessToken]);

  useEffect(() => {
    if (accessToken) download();
  }, [fileName, accessToken]);

  // Nothing to show while the BE is working or while the browser is leaving
  // for the signed URL - just a spinner. The card is for failures only.
  if (status !== STATUS.ERROR) {
    return (
      <Wrapper>
        <Box className="loading">
          <CircularProgress size={28} />
          <Typography className="loading-text">
            {status === STATUS.DONE
              ? 'Your file is opening...'
              : 'Preparing your file...'}
          </Typography>
        </Box>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Box className="card">
        <Typography variant="h6" className="title">
          Unable to open file
        </Typography>

        {!!fileName && (
          <Typography className="file-name" title={fileName}>
            {fileName.slice(fileName.lastIndexOf('/') + 1)}
          </Typography>
        )}

        <Typography className="state-text error">{errorMessage}</Typography>

        {!!fileName && (
          <Button
            variant="contained"
            color="primary"
            className="action"
            onClick={download}
          >
            Try again
          </Button>
        )}
      </Box>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  /* Rendered with noLayout: no sidebar or header, so own the whole viewport. */
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  background: #fafafa;

  .loading {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .loading-text {
    font-size: 14px;
    color: #666;
  }
  .card {
    width: 100%;
    max-width: 480px;
    padding: 32px;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    text-align: center;
  }
  .title {
    font-weight: 600;
  }
  .file-name {
    margin-top: 8px;
    color: #666;
    font-size: 14px;
    word-break: break-all;
  }
  .state-text {
    margin-top: 24px;
    font-size: 14px;
    color: #666;
  }
  .state-text.error {
    color: #c62828;
  }
  .action {
    margin-top: 24px;
  }
`;

export default DownloadPlatoFiles;
